import React, { useEffect } from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import Button from '@atlaskit/button';
import AddIcon from '@atlaskit/icon/core/migration/add';
import { SkeletonItem } from '@atlaskit/menu';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import type { async_softwareReleasesApprovers_Approvers$key } from '@atlassian/jira-relay/src/__generated__/async_softwareReleasesApprovers_Approvers.graphql';
import { RIGHT_SIDEBAR_WIDTH } from '@atlassian/jira-software-releases-version-detail-shared/src/common/constants.tsx';
import { lazyAfterPaint, useLazyPhase } from '@atlassian/react-loosely-lazy';
import { APPROVERS_SHOW_MORE_THRESHOLD } from './constants.tsx';
import messages from './messages.tsx';

const PADDING_SIZE = gridSize * 3;
const BORDER_SIZE = 1;
const WIDTH_OFFSET = (PADDING_SIZE + BORDER_SIZE) * 2;

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const LazyApprovers = lazyAfterPaint(
	() =>
		import(/* webpackChunkName: "async-releases-approvers" */ './index').then(
			(module) => module.Approvers,
		),
	{ ssr: false },
);

type Props = {
	jiraVersion: async_softwareReleasesApprovers_Approvers$key;
};

export const Approvers = ({ jiraVersion }: Props) => {
	const { startNextPhase } = useLazyPhase();
	useEffect(() => {
		startNextPhase();
	}, [startNextPhase]);

	const version = useFragment(
		graphql`
			fragment async_softwareReleasesApprovers_Approvers on JiraVersion {
				...ui_softwareReleasesApprovers_Approvers
			}
		`,
		jiraVersion,
	);

	const { formatMessage } = useIntl();

	const numberOfApproversToShow = APPROVERS_SHOW_MORE_THRESHOLD;

	return (
		<Placeholder
			name="releases-approvers"
			fallback={
				<ApproversCardWrapper>
					<Header>
						<ApproversTitle>{formatMessage(messages.approversTitle)}</ApproversTitle>
						<Button iconBefore={<AddIcon LEGACY_size="small" label="" />} appearance="subtle" />
					</Header>
					{[...Array(numberOfApproversToShow).keys()].map((i) => (
						<SkeletonItem key={i} isShimmering hasAvatar />
					))}
				</ApproversCardWrapper>
			}
		>
			<LazyApprovers jiraVersion={version} />
		</Placeholder>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const ApproversTitle = styled.div({
	display: 'flex',
	alignItems: 'center',
	font: token('font.body'),
	fontWeight: token('font.weight.semibold'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text', colors.N300),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const Header = styled.div({
	display: 'flex',
	justifyContent: 'space-between',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const ApproversCardWrapper = styled.div({
	gap: token('space.200'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	maxWidth: `${RIGHT_SIDEBAR_WIDTH - WIDTH_OFFSET}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	maxHeight: gridSize * 49,
	overflow: 'auto',
	paddingTop: token('space.300'),
	paddingRight: token('space.300'),
	paddingBottom: token('space.300'),
	paddingLeft: token('space.300'),
});
